// LogsPage
// --------
// Read-only view of powermeter logs for a single IMEI. Reached via the
// "View logs" row action from /devices or any per-company devices tab.
//
// Design notes:
//   - No total-count read. The server reports `hasMore` per page; we
//     append on Load More until it goes false.
//   - Filter strip: a single datetime input + sort dropdown + Submit /
//     Refresh buttons. The date acts as an *anchor*:
//        * sort=desc → sent as `to`   (logs at or before this moment)
//        * sort=asc  → sent as `from` (logs at or after this moment)
//     Empty date = no bound.
//   - Columns come from the same per-device-type field maps used by the
//     live Status panel (statusMapper.js) — same labels, same formatters.
//     Header rows are flattened out, IMEI / device_type / date are
//     skipped (we already render them in the header / dedicated column).

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  Tooltip,
  Chip,
  TextField,
  MenuItem,
  Popover,
  FormControlLabel,
  Checkbox,
  Divider,
  Link as MuiLink,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import ViewColumnIcon from "@mui/icons-material/ViewColumnOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import api from "../../services/api";
import errMsg from "../../services/errMsg";
import toast from "react-hot-toast";
import {
  fieldsForDeviceType,
  formatStatusValue,
} from "../companyDevices/statusMapper";
import { updatePreferences } from "../auth/authSlice";

const PAGE_LIMIT = 100;
const SKIP_KEYS = new Set(["device_type", "imei", "date"]);

// On/off-style values (mapped by statusMapper) tint green or red so an
// admin scanning a long log table can spot transitions at a glance.
// Matches values produced by the field maps in statusMapper.js —
// "ON"/"OFF" for motor/valve, "Online"/"Offline" for the connection.
const colorForValue = (formatted) => {
  const v = String(formatted ?? "").trim().toUpperCase();
  if (v === "ON" || v === "ONLINE") return "success.main";
  if (v === "OFF" || v === "OFFLINE") return "error.main";
  return null;
};

const formatLogDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch (_) {
    return iso;
  }
};

// Type label for the header chip — kept tiny so it doesn't drift if
// status labels are renamed elsewhere.
const TYPE_LABEL = { 1: "Motor", 2: "Tank", 3: "Valve", 4: "Flow" };

// Convert <input type="datetime-local"> string ("YYYY-MM-DDTHH:mm") to
// an ISO UTC string the API can parse. Empty input → null.
const localToIso = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

// "YYYY-MM-DDTHH:mm" for the datetime-local input, in *local* time (not
// UTC) so what the user sees in the picker matches the clock on their
// wall. Used to pre-fill the Date filter on first mount.
const nowLocalForInput = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function LogsPage() {
  const { imei } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userPrefs = useSelector((s) => s.auth.user?.preferences || {});

  // The two halves of the page state:
  //   - `applied` is the filter set the table is currently showing
  //   - `pending` is what the filter strip controls (Submit copies pending → applied)
  // Default both to "now (local)" + descending — so the page opens
  // anchored to the current moment, newest record first. Captured at
  // mount; the user can change it and click Submit to re-anchor.
  const initialFilter = useMemo(
    () => ({ date: nowLocalForInput(), sort: "desc" }),
    []
  );
  const [pending, setPending] = useState(initialFilter);
  const [applied, setApplied] = useState(initialFilter);

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [device, setDevice] = useState(null);
  const reqIdRef = useRef(0);

  // Device meta for the header
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/devices/imei/${encodeURIComponent(imei)}`
        );
        if (!cancelled) setDevice(data?.data?.device || null);
      } catch (_) {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [imei]);

  // All available columns for this device's type (the full set the
  // Status panel would render). Header rows + IMEI/type/date stripped.
  const allColumns = useMemo(() => {
    const type = device?.device_type ?? 1;
    return fieldsForDeviceType(type).filter(
      (f) => f.type !== "header" && !SKIP_KEYS.has(f.key)
    );
  }, [device]);

  // Visibility list for the current device_type, sourced from per-user
  // preferences. `undefined` means "no saved preference" → show every
  // column. An explicit empty array means the user hid them all.
  const storedKeys =
    userPrefs?.logsColumns?.[String(device?.device_type ?? "")];

  const columns = useMemo(() => {
    if (!Array.isArray(storedKeys)) return allColumns;
    const allowed = new Set(storedKeys);
    return allColumns.filter((c) => allowed.has(c.key));
  }, [allColumns, storedKeys]);


  // Columns popover
  const [colsAnchor, setColsAnchor] = useState(null);
  const colsOpen = Boolean(colsAnchor);
  // While the popover is open, edits are buffered in `pendingCols` and
  // saved either on every toggle (debounced via the parent) or on close.
  // Here we save on every toggle for instant persistence — small writes.
  const toggleColumn = (key) => {
    if (!device?.device_type) return;
    const type = String(device.device_type);
    const current = Array.isArray(storedKeys)
      ? storedKeys
      : allColumns.map((c) => c.key); // default to all visible
    const next = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key];
    dispatch(
      updatePreferences({
        logsColumns: {
          ...(userPrefs.logsColumns || {}),
          [type]: next,
        },
      })
    );
  };
  const resetColumns = () => {
    if (!device?.device_type) return;
    const type = String(device.device_type);
    const { [type]: _drop, ...rest } = userPrefs.logsColumns || {};
    dispatch(
      updatePreferences({ logsColumns: rest })
    );
  };
  const showAllColumns = () => {
    if (!device?.device_type) return;
    const type = String(device.device_type);
    dispatch(
      updatePreferences({
        logsColumns: {
          ...(userPrefs.logsColumns || {}),
          [type]: allColumns.map((c) => c.key),
        },
      })
    );
  };

  // The actual fetcher. `append` controls whether results replace the
  // list (initial / Submit / Refresh) or are concatenated (Load More).
  const runFetch = async (nextPage, currentApplied, { append } = { append: false }) => {
    setLoading(true);
    const myReq = ++reqIdRef.current;
    try {
      const params = {
        page: nextPage,
        limit: PAGE_LIMIT,
        sort: currentApplied.sort,
      };
      const iso = localToIso(currentApplied.date);
      if (iso) {
        // Date is the anchor; what side it bounds depends on sort.
        if (currentApplied.sort === "asc") params.from = iso;
        else params.to = iso;
      }
      const { data } = await api.get(
        `/devices/imei/${encodeURIComponent(imei)}/logs`,
        { params }
      );
      if (myReq !== reqIdRef.current) return;
      const payload = data?.data || {};
      setItems((prev) =>
        append ? [...prev, ...(payload.items || [])] : payload.items || []
      );
      setPage(payload.pagination?.page || nextPage);
      setHasMore(Boolean(payload.pagination?.hasMore));
    } catch (err) {
      if (myReq === reqIdRef.current) {
        toast.error(errMsg(err, "Failed to load logs"));
      }
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  };

  // Initial load — no filter applied
  useEffect(() => {
    runFetch(1, applied, { append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imei]);

  const onSubmit = () => {
    if (loading) return;
    setApplied(pending);
    setItems([]);
    setPage(1);
    runFetch(1, pending, { append: false });
  };

  const onRefresh = () => {
    if (loading) return;
    runFetch(1, applied, { append: false });
  };

  const onLoadMore = () => {
    if (loading || !hasMore) return;
    runFetch(page + 1, applied, { append: true });
  };

  // Reasonable maximum for datetime-local input — let user pick anywhere
  // in the past or future. No min bound.
  const hasFilters = applied.date.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: { md: 1 },
        minHeight: 0,
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          flex: { md: 1 },
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header band */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            p: 1.25,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, lineHeight: 1.2 }}
            >
              Logs
              <Box
                component="span"
                sx={{
                  ml: 1,
                  fontFamily: "monospace",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  color: "text.secondary",
                }}
              >
                {imei}
              </Box>
            </Typography>
            {device && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.1 }}
              >
                {device.device_name || "(no name)"} ·{" "}
                {TYPE_LABEL[device.device_type] || "type?"} ·{" "}
                {device.company?.name || "— unassigned —"}
              </Typography>
            )}
          </Box>
          {/* "X" close on the right — matches StatusPanel + dialog
              dismiss affordances so the layout feels uniform. Still
              navigates back. */}
          <Tooltip title="Close">
            <IconButton size="small" onClick={() => navigate(-1)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Filter strip */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{
            p: 1.25,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
            backgroundColor: "background.default",
          }}
        >
          <TextField
            type="datetime-local"
            size="small"
            label="Date"
            value={pending.date}
            onChange={(e) =>
              setPending((p) => ({ ...p, date: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 220 }}
          />
          <TextField
            select
            size="small"
            label="Sort order"
            value={pending.sort}
            onChange={(e) =>
              setPending((p) => ({ ...p, sort: e.target.value }))
            }
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="desc">Descending</MenuItem>
            <MenuItem value="asc">Ascending</MenuItem>
          </TextField>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={loading}
            sx={{ flexShrink: 0 }}
          >
            Submit
          </Button>
          <Tooltip title="Refresh current view">
            <span>
              <IconButton onClick={onRefresh} disabled={loading} size="small">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Choose columns">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ViewColumnIcon fontSize="small" />}
                onClick={(e) => setColsAnchor(e.currentTarget)}
                disabled={allColumns.length === 0}
                sx={{ flexShrink: 0 }}
              >
                Columns ({columns.length}/{allColumns.length})
              </Button>
            </span>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          {hasFilters && (
            <Chip
              size="small"
              label="Filter active"
              color="primary"
              variant="outlined"
              sx={{ height: 22, fontSize: "0.75rem" }}
            />
          )}
          <Chip
            size="small"
            label={`${items.length} loaded`}
            sx={{ height: 22, fontSize: "0.75rem" }}
          />
        </Stack>

        {/* Columns popover — checkbox per available field. Saves to the
            admin's preferences on every toggle, scoped by device_type so
            motor and tank can have independent column sets. */}
        <Popover
          open={colsOpen}
          anchorEl={colsAnchor}
          onClose={() => setColsAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: { width: 280, maxHeight: 420, overflowY: "auto" },
          }}
        >
          <Box
            sx={{
              p: 1,
              position: "sticky",
              top: 0,
              backgroundColor: "background.paper",
              borderBottom: "1px solid",
              borderColor: "divider",
              zIndex: 1,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                Visible columns
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <MuiLink
                  component="button"
                  onClick={showAllColumns}
                  sx={{ fontSize: "0.6875rem", fontWeight: 600 }}
                >
                  All
                </MuiLink>
                <MuiLink
                  component="button"
                  onClick={resetColumns}
                  sx={{ fontSize: "0.6875rem", fontWeight: 600 }}
                >
                  Reset
                </MuiLink>
              </Stack>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", fontSize: "0.6875rem", mt: 0.25 }}
            >
              Saved per device type for your account.
            </Typography>
          </Box>
          <Box sx={{ p: 0.5 }}>
            {allColumns.map((c) => {
              const visible = Array.isArray(storedKeys)
                ? storedKeys.includes(c.key)
                : true;
              return (
                <FormControlLabel
                  key={c.key + c.label}
                  control={
                    <Checkbox
                      size="small"
                      checked={visible}
                      onChange={() => toggleColumn(c.key)}
                    />
                  }
                  label={
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          fontSize: "0.8125rem",
                          fontWeight: 500,
                          lineHeight: 1.2,
                        }}
                      >
                        {c.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.625rem",
                        }}
                      >
                        {c.key}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    display: "flex",
                    m: 0,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                />
              );
            })}
            {allColumns.length === 0 && (
              <Typography
                variant="caption"
                sx={{ p: 1.5, display: "block", color: "text.secondary" }}
              >
                No columns available for this device type.
              </Typography>
            )}
          </Box>
          <Divider />
          <Box sx={{ p: 1, display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              onClick={() => setColsAnchor(null)}
              variant="text"
            >
              Done
            </Button>
          </Box>
        </Popover>

        {/* Table area — grows; scrolls internally */}
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
          }}
        >
          {/* Compact log table: tighter cell padding, smaller font, header
              text allowed to wrap. Header gets a teal band so it stands
              out against the body; even body rows get a faint teal tint
              for easier left-to-right scanning. `minWidth` on the table
              forces it past the container width on dense column sets, so
              the horizontal scrollbar always appears when columns are
              many — even on wide monitors. */}
          <Table
            stickyHeader
            size="small"
            sx={{
              // Sum of the # col + date col + room per data column. The
              // 76px-per-column figure roughly matches the minWidth/maxWidth
              // bounds set on each header cell below.
              minWidth: 36 + 130 + columns.length * 76,
              "& .MuiTableCell-root": {
                px: 0.75,
                py: 0.5,
                fontSize: "0.7rem",
                lineHeight: 1.2,
                // Center everything — easier to scan numeric readings
                // when they sit in the middle of their column instead of
                // hugging the left edge.
                textAlign: "center",
              },
              "& .MuiTableCell-head": {
                fontSize: "0.6875rem",
                fontWeight: 700,
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.15,
                verticalAlign: "middle",
                color: "common.white",
                backgroundColor: "primary.main",
                borderBottom: "1px solid",
                borderColor: "primary.dark",
              },
              // Zebra rows on the body only — leave the sticky header
              // alone. Hover wins over the zebra tint.
              "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
                backgroundColor: "rgba(13, 148, 136, 0.06)",
              },
              "& .MuiTableBody-root .MuiTableRow-root:hover": {
                backgroundColor: "rgba(13, 148, 136, 0.14)",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 36 }}>#</TableCell>
                <TableCell sx={{ width: 130, whiteSpace: "nowrap !important" }}>
                  Date
                </TableCell>
                {columns.map((c) => (
                  <TableCell
                    key={c.key + c.label}
                    // minWidth chosen so 3-digit values + units fit without
                    // letting the column stretch wide. maxWidth caps the
                    // header so it wraps onto 2 lines for longer labels.
                    sx={{ minWidth: 56, maxWidth: 90 }}
                  >
                    {c.label}
                    {/* Slot key in parens on its own line — same
                        "Label (key)" reference style used in the Status
                        panel rows and the column-selection popover. */}
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        fontFamily: "monospace",
                        fontSize: "0.5625rem",
                        fontWeight: 600,
                        opacity: 0.75,
                        mt: 0.25,
                      }}
                    >
                      ({c.key})
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={2 + columns.length}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={2 + columns.length}
                    align="center"
                    sx={{ py: 6, color: "text.secondary" }}
                  >
                    <HistoryIcon sx={{ fontSize: 32, opacity: 0.4, mb: 0.5 }} />
                    <Typography variant="caption" display="block">
                      No data
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ fontSize: "0.6875rem", color: "text.disabled" }}
                    >
                      {hasFilters
                        ? "Try a different date / sort order."
                        : "This IMEI hasn't reported any logs yet."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {items.map((row, i) => (
                <TableRow key={row._id || i} hover>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {i + 1}
                  </TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      fontFamily: "monospace",
                    }}
                  >
                    {formatLogDate(row.date || row.createdAt)}
                  </TableCell>
                  {columns.map((c) => {
                    const v = row[c.key];
                    const isEmpty = v === undefined || v === null || v === "";
                    const formatted = isEmpty ? "" : formatStatusValue(c, v);
                    const statusColor = isEmpty
                      ? null
                      : colorForValue(formatted);
                    return (
                      <TableCell
                        key={c.key + c.label}
                        sx={{
                          // Allow wrap on long string values, but most
                          // readings are 3-digit numbers — they stay on one
                          // line and the column collapses to its content.
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          fontFamily: "monospace",
                          // Tint on/off-style values for at-a-glance scan.
                          ...(statusColor && {
                            color: statusColor,
                            fontWeight: 700,
                          }),
                        }}
                      >
                        {formatted}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Load-more footer (hidden when nothing more to load AND we already have rows) */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{
            p: 1.25,
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          {hasMore ? (
            <Button
              variant="contained"
              onClick={onLoadMore}
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={14} color="inherit" /> : null
              }
              sx={{ minWidth: 160 }}
            >
              {loading ? "Loading…" : "Load more"}
            </Button>
          ) : items.length > 0 ? (
            <Typography variant="caption" color="text.secondary">
              All matching logs loaded
            </Typography>
          ) : null}
        </Stack>
      </Paper>
    </Box>
  );
}
